import * as React from 'react';
import { useAtomValue } from 'jotai';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';

import { activeRestaurantOnboardingStateAtom, RestaurantOnboardingStage } from '.';
import GoogleAddressInputResult, { googleAddresInputValueAtom } from './google-address-input-result';
import RestaurantOnboardingAiMagicLoading from './restaurant-onboarding-ai-magic-loading-indicator';
import RestaurantOnboardingForm, { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import RestaurantOnboardingSuccessful from './restaurant-onboarding-successful';
import RestaurantOrContactAlreadyExists from './restaurant-already-exist';
import RestaurantOrganizationOnboardingContactForm from './restaurant-onboarding-contact-form';
import { restaurantInformationDataAtom } from './use-restaurant-onboarding-data-query';
import RestaurantOwnerOTPValidation from './restaurant-owner-otp-validation';
import { extractRestaurantsInfo, useEmployerRestaurantsData } from '../hooks';
import { useGettingStartedPage } from './restaurant-onboarding-modal.hooks';
import EstablishmentNotHORECA from './establishment-not-horeca';

// Styled Components
export const RestaurantOnboardingWrapper = styled.form<{
  isInputFilled: boolean;
  activeUI: RestaurantOnboardingStage;
  isGettingStartedPage?: boolean;
}>`
  ${tw`w-full flex flex-row items-center justify-center`}
  ${tw`md:(mx-auto max-w-[38rem] min-w-[38rem])`};
  ${({ activeUI }) => activeUI !== 'restaurant_onboarding_successfull' && tw`pt-[40px] pb-[32px]`}
  ${({ isInputFilled, activeUI }) =>
    ((activeUI === 'select_restaurant_from_google' && !isInputFilled) ||
      [
        'restaurant_already_exists',
        'contact_already_exists',
        'restaurant_onboarding_successfull',
        'otp_verification',
      ].includes(activeUI)) &&
    tw`h-full py-0`}
  ${({ activeUI, isGettingStartedPage }) =>
    activeUI === 'restaurant_onboarding_successfull' && isGettingStartedPage && tw`md:min-w-full max-w-full`}
`;
// eslint-disable-next-line @typescript-eslint/no-empty-interface
const RestaurantOnboarding = ({ methods }: { methods: UseFormReturn<RestaurantOnboardingPayload, any> }) => {
  const activeUI = useAtomValue(activeRestaurantOnboardingStateAtom);
  const googleInputAddressValue = useAtomValue(googleAddresInputValueAtom);
  const isInputFilled = googleInputAddressValue.length > 0;
  const { t } = useTranslation('restaurant-onboarding');
  const registeredRestaurantData = useAtomValue(restaurantInformationDataAtom);

  const restaurantQueryData = useEmployerRestaurantsData();
  const { restaurantData } = extractRestaurantsInfo(restaurantQueryData);

  const selfRegisteredVenue = React.useMemo(
    () =>
      registeredRestaurantData?.organization_id &&
      restaurantData.find((v) => v.restaurant.organization_id === registeredRestaurantData?.organization_id),
    [registeredRestaurantData?.organization_id, restaurantData]
  );

  const isGettingStartedPage = useGettingStartedPage();
  return (
    <FormProvider {...methods}>
      <RestaurantOnboardingWrapper
        isGettingStartedPage={isGettingStartedPage}
        activeUI={activeUI}
        isInputFilled={isInputFilled}
      >
        {activeUI === 'select_restaurant_from_google' && <GoogleAddressInputResult />}
        {activeUI === 'magic_ai_loading' && <RestaurantOnboardingAiMagicLoading />}
        {activeUI === 'restaurant_onboarding_form' && <RestaurantOnboardingForm />}
        {activeUI === 'restaurant_already_exists' && (
          <RestaurantOrContactAlreadyExists
            title={t('looks_like_this_venue_already_exists_on_restworld')}
            subtitle={t(selfRegisteredVenue ? 'you_have_already_registered_venue' : 'someone_already_registered', {
              venueName: registeredRestaurantData?.restaurant_name,
              venueCity: registeredRestaurantData?.locality || selfRegisteredVenue?.restaurant?.address,
            })}
            key="restaurant_already_exists"
          />
        )}
        {activeUI === 'restaurant_owner_onboarding_form' && <RestaurantOrganizationOnboardingContactForm />}
        {activeUI === 'contact_already_exists' && (
          <RestaurantOrContactAlreadyExists
            title={t('contacts_already_exists')}
            subtitle={t('contacts_info_registered')}
            key="contact_already_exists"
          />
        )}
        {activeUI === 'otp_verification' && <RestaurantOwnerOTPValidation />}
        {activeUI === 'restaurant_onboarding_successfull' && <RestaurantOnboardingSuccessful />}
        {activeUI === 'not_horeca' && <EstablishmentNotHORECA />}
      </RestaurantOnboardingWrapper>
    </FormProvider>
  );
};

export default RestaurantOnboarding;
